import { memo, useRef } from 'react';
import { Image as ImageIcon, Film, Upload, Link } from 'lucide-react';

const MediaTab = memo(({ mediaItems, swappingMedia, setSwappingMedia, mediaVideoUrl, setMediaVideoUrl, onMediaSwap, onMediaUploadAndSwap, t, theme }) => {
  const mediaFileRef = useRef(null);

  return (
    <div>
      <input type="file" ref={mediaFileRef} accept="image/*" style={{ display: 'none' }} onChange={e => {
        const file = e.target.files?.[0];
        if (file && swappingMedia !== null) {
          const item = mediaItems[swappingMedia];
          if (item) onMediaUploadAndSwap(file, item.url);
        }
        e.target.value = '';
      }} />

      {mediaItems.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 12px' }}>
          <ImageIcon size={28} style={{ color: t.muted, margin: '0 auto 12px', opacity: 0.4 }} />
          <p style={{ fontFamily: 'var(--serif)', fontSize: '15px', color: t.fg, marginBottom: '4px' }}>No media found</p>
          <p style={{ fontSize: '12px', color: t.muted, fontFamily: 'var(--sans)' }}>Generate a site first, then swap your own photos and videos here.</p>
        </div>
      ) : (
        <>
          <p style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: t.muted, marginBottom: '14px' }}>
            {mediaItems.length} media items — tap Replace to swap
          </p>
          {(() => {
            const groups = {};
            mediaItems.forEach(m => { if (!groups[m.section]) groups[m.section] = []; groups[m.section].push(m); });
            return Object.entries(groups).map(([section, items]) => (
              <div key={section} style={{ marginBottom: '18px' }}>
                <p style={{ fontFamily: 'var(--sans)', fontSize: '11px', fontWeight: 500, color: t.gold, marginBottom: '8px', textTransform: 'capitalize' }}>
                  {section}
                </p>
                {items.map(item => (
                  <div key={item.index} data-testid={`media-item-${item.index}`} style={{
                    display: 'flex', alignItems: 'center', gap: '10px', padding: '10px',
                    marginBottom: '6px', borderRadius: '10px',
                    border: `1px solid ${swappingMedia === item.index ? 'rgba(201,168,76,0.25)' : t.border}`,
                    background: swappingMedia === item.index ? 'rgba(201,168,76,0.04)' : (theme === 'light' ? '#fff' : 'rgba(242,239,233,0.02)'),
                    transition: 'all .2s',
                  }}>
                    {/* Thumbnail */}
                    {item.type === 'image' ? (
                      <div style={{ width: '48px', height: '48px', flexShrink: 0, borderRadius: '8px', overflow: 'hidden', background: 'rgba(242,239,233,0.04)' }}>
                        <img src={item.url} alt={item.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                      </div>
                    ) : (
                      <div style={{ width: '48px', height: '48px', flexShrink: 0, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(201,168,76,0.06)' }}>
                        <Film size={16} style={{ color: t.gold }} />
                      </div>
                    )}

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: t.fg, marginBottom: '2px' }}>{item.label}</p>
                      <p style={{ fontFamily: 'var(--sans)', fontSize: '10px', color: t.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: 0.6 }}>
                        {item.url.length > 35 ? '...' + item.url.slice(-32) : item.url}
                      </p>
                      {/* Expanded swap controls */}
                      {swappingMedia === item.index && (
                        <div style={{ marginTop: '8px' }}>
                          {item.type === 'image' ? (
                            <button onClick={() => mediaFileRef.current?.click()} style={{
                              padding: '7px 14px', fontSize: '11px', fontFamily: 'var(--sans)', fontWeight: 500,
                              background: t.gold, color: '#000', border: 'none', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: '5px', borderRadius: '8px',
                              transition: 'all .15s', boxShadow: '0 2px 6px rgba(201,168,76,0.15)',
                            }}>
                              <Upload size={12} /> Choose File
                            </button>
                          ) : (
                            <div style={{ display: 'flex', gap: '5px' }}>
                              <input type="text" value={mediaVideoUrl} onChange={e => setMediaVideoUrl(e.target.value)} placeholder="Paste Vimeo or YouTube URL"
                                style={{ flex: 1, padding: '7px 10px', fontSize: '11px', fontFamily: 'var(--sans)', background: t.inputBg || 'rgba(242,239,233,0.04)', border: `1px solid ${t.border}`, color: t.fg, outline: 'none', borderRadius: '8px' }}
                                data-testid={`media-video-input-${item.index}`} />
                              <button onClick={() => {
                                let url = mediaVideoUrl.trim();
                                if (url.includes('youtube.com/watch?v=')) { try { url = `https://www.youtube.com/embed/${new URL(url).searchParams.get('v')}?autoplay=1&mute=1&loop=1`; } catch {} }
                                else if (url.includes('youtu.be/')) { url = `https://www.youtube.com/embed/${url.split('youtu.be/')[1]?.split('?')[0]}?autoplay=1&mute=1&loop=1`; }
                                else if (url.includes('vimeo.com/') && !url.includes('player.vimeo.com')) { url = `https://player.vimeo.com/video/${url.split('vimeo.com/')[1]?.split(/[?/]/)[0]}?background=1&autoplay=1&loop=1&muted=1`; }
                                onMediaSwap(item.url, url);
                              }} disabled={!mediaVideoUrl.trim()} style={{
                                padding: '7px 12px', background: mediaVideoUrl.trim() ? t.gold : t.faint,
                                color: mediaVideoUrl.trim() ? '#000' : t.muted, border: 'none',
                                cursor: mediaVideoUrl.trim() ? 'pointer' : 'default', borderRadius: '8px',
                              }}>
                                <Link size={12} />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Replace button */}
                    <button onClick={() => { setSwappingMedia(swappingMedia === item.index ? null : item.index); setMediaVideoUrl(''); }}
                      data-testid={`media-replace-${item.index}`}
                      style={{
                        flexShrink: 0, padding: '6px 12px', fontFamily: 'var(--sans)', fontSize: '11px', fontWeight: 500,
                        background: swappingMedia === item.index ? 'rgba(201,168,76,0.1)' : 'transparent',
                        border: `1px solid ${swappingMedia === item.index ? 'rgba(201,168,76,0.25)' : t.border}`,
                        color: swappingMedia === item.index ? t.gold : t.muted,
                        cursor: 'pointer', transition: 'all .2s', borderRadius: '8px',
                      }}
                      onMouseEnter={e => { if (swappingMedia !== item.index) { e.currentTarget.style.background = 'rgba(201,168,76,0.06)'; e.currentTarget.style.borderColor = 'rgba(201,168,76,0.15)'; e.currentTarget.style.color = t.gold; }}}
                      onMouseLeave={e => { if (swappingMedia !== item.index) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = t.border; e.currentTarget.style.color = t.muted; }}}
                    >
                      {swappingMedia === item.index ? 'Cancel' : 'Replace'}
                    </button>
                  </div>
                ))}
              </div>
            ));
          })()}
        </>
      )}
    </div>
  );
});

MediaTab.displayName = 'MediaTab';
export default MediaTab;
